/**
 * Ponto ÚNICO de integração com os módulos paralelos:
 * - focos (src/fires): `FirePanel`, `useFireLayers`
 * - vetores (src/vectors): `VectorPanel`, `useVectorLayers`
 * - exportação (src/export): `ExportDialog` ({ open, onClose })
 *
 * Importar este arquivo também registra os codecs de URL desses módulos (eles
 * se registram no import dos seus stores), por isso `main.tsx` o importa antes
 * de ler a URL.
 *
 * Cada painel roda dentro de um `ModuleBoundary`: se um módulo falhar, só a
 * aba dele mostra o erro.
 *
 * Os painéis são REGISTRADOS aqui (`registerPanel`): o trilho do desktop e as
 * abas da folha do celular leem do registro. Um módulo novo (Previsão,
 * Estações, Sondagens) entra com mais um `registerPanel`, sem tocar no layout.
 */
import { AirVent, CloudRain, Droplets, Flame, Haze, Map as MapIcon, Radar, Satellite, Ship, Thermometer, TowerControl, Waves, Zap } from 'lucide-react';
import { lazy, Suspense, useEffect } from 'react';
import type { StoreApi, UseBoundStore } from 'zustand';
// Stores com os codecs de URL `f` (focos), `a` (áreas dos últimos focos), `m`
// (vetores), `s` (estações), `r` (sondagem), `n` (embarcações), `e` (radar), `u` (fumaça),
// `w` (estações meteorológicas), `p` (qualidade do ar), `j` (chuva por satélite), `k` (rastreio de nuvens) e `i` (rastreio da chuva): import explícito para
// garantir o registro antes de `readUrlState()`.
import '../ar/store';
import '../fires/store';
import '../fires/areaFilter';
import '../gsmap/store';
import '../meteo/store';
import '../radar/store';
import '../rivers/store';
import '../ships/store';
import '../smoke/store';
import '../raios/store';
import '../sounding/store';
import '../stations/store';
import '../tracking/store';
import '../rastreioChuva/store';
import '../busca/store';
// Codecs `y` (variável) e `d` (instante) do "Próximos dias".
import '../previsao/store';
import { usePrevisaoLayers } from '../previsao/usePrevisaoLayers';
import { useBuscaLayer } from '../busca/useBuscaLayer';
import '../vectors/store';
import { ArPanel } from '../ar/ArPanel';
import { useArStore } from '../ar/store';
import { useArDesatualizado } from '../ar/sync';
import { refreshAr, useArLayers } from '../ar/useArLayers';
import { FirePanel } from '../fires/FirePanel';
import { GsmapPanel } from '../gsmap/GsmapPanel';
import { useGsmapStore } from '../gsmap/store';
import { useGsmapDesatualizado, useGsmapResumo } from '../gsmap/sync';
import { refreshGsmap, useGsmapLayers } from '../gsmap/useGsmapLayers';
import { RadarPanel } from '../radar/RadarPanel';
import { useRadarStore } from '../radar/store';
import { useRadarDesatualizado } from '../radar/sync';
import { refreshRadar, useRadarLayers } from '../radar/useRadarLayers';
import { refreshFires, useFireLayers } from '../fires/useFireLayers';
import { MeteoPanel } from '../meteo/MeteoPanel';
import { useMeteoStore } from '../meteo/store';
import { useMeteoDesatualizado } from '../meteo/sync';
import { refreshMeteo, useMeteoLayers } from '../meteo/useMeteoLayers';
import { RiverPanel } from '../rivers/RiverPanel';
import { useRiosDesatualizados } from '../rivers/sync';
import { refreshRios, useRiverLayers } from '../rivers/useRiverLayers';
import { ShipPanel } from '../ships/ShipPanel';
import { SmokePanel } from '../smoke/SmokePanel';
import { useSmokeStore } from '../smoke/store';
import { useFumacaDesatualizada } from '../smoke/sync';
import { refreshFumaca, useSmokeLayers } from '../smoke/useSmokeLayers';
import { RaiosPanel } from '../raios/RaiosPanel';
import { useRaiosStore } from '../raios/store';
import { useRaiosDesatualizados } from '../raios/sync';
import { refreshRaios, useRaiosLayers } from '../raios/useRaiosLayers';
import { useNaviosDesatualizados } from '../ships/sync';
import { refreshNavios, useShipLayers } from '../ships/useShipLayers';
import { StationPanel } from '../stations/StationPanel';
import { useEstacoesDesatualizadas } from '../stations/sync';
import { refreshEstacoes, useStationLayers } from '../stations/useStationLayers';
import { SoundingPanel } from '../sounding/SoundingPanel';
import { iniciarFluxoPrevisao } from '../tracking/fluxo';
import { refreshRastreio, useTrackingLayers } from '../tracking/useTrackingLayers';
import { refreshRastreioChuva, useRastreioChuvaLayers } from '../rastreioChuva/sync';
import { refreshSondagens, useSoundingLayers } from '../sounding/useSoundingLayers';
import { registerRefreshHook } from '../loop/autoRefresh';
import { useLoopStore } from '../loop/store';
import { useUiStore } from '../state/ui';
import { VectorPanel } from '../vectors/VectorPanel';
import { useVectorLayers } from '../vectors/useVectorLayers';
import { useFiresInError } from './fireErrorNotice';
import { ModuleBoundary } from './ModuleBoundary';
import { useFireStore } from '../fires/store';
import { useRiverStore } from '../rivers/store';
import { useShipStore } from '../ships/store';
import { useSoundingStore } from '../sounding/store';
import { useStationStore } from '../stations/store';
import { useGoesStore } from '../state/goes';
import { useVectorStore } from '../vectors/store';
import { getProductName } from '../config/products';
import { DEF_PRODUTO } from '../radar/produtos';
import { VECTOR_LAYERS } from '../vectors/catalog';
import { registerPanel, type LayerSwitch } from './panels';
import { SatellitePanel } from './SatellitePanel';

// ---------------------------------------------------------------------------
// Interruptores das camadas (trilho, abas e chips): um clique liga/desliga a
// camada inteira; sub-produtos e aparência ficam no painel.
// ---------------------------------------------------------------------------

/**
 * O interruptor a partir da store da camada: `ligada` é o mesmo seletor para o
 * hook (trilho, abas) e para a leitura fora do React (o "Próximos dias").
 */
function interruptor<S>(store: UseBoundStore<StoreApi<S>>, ligada: (s: S) => boolean, setOn: (on: boolean) => void): LayerSwitch {
  return { useOn: () => store(ligada), isOn: () => ligada(store.getState()), setOn };
}

/** Pilha do satélite: alguma imagem visível. Desligar esconde todas; religar restaura a seleção. */
const satelliteSwitch = interruptor(useGoesStore, (s) => s.layers.some((l) => l.visible), (on) => useGoesStore.getState().setStackVisible(on));
/** Focos: as duas fontes (GOES-19 10 min e diário) são sub-produtos; religar volta à seleção anterior. */
const fireSwitch = interruptor(useFireStore, (s) => s.sources.goes10min || s.sources.diario, (on) => useFireStore.getState().setAllSources(on));
const radarSwitch = interruptor(useRadarStore, (s) => s.visivel, (on) => useRadarStore.getState().setVisivel(on));
const gsmapSwitch = interruptor(useGsmapStore, (s) => s.visivel, (on) => useGsmapStore.getState().setVisivel(on));
const smokeSwitch = interruptor(useSmokeStore, (s) => s.visivel, (on) => useSmokeStore.getState().setVisivel(on));
const raiosSwitch = interruptor(useRaiosStore, (s) => s.visivel, (on) => useRaiosStore.getState().setVisivel(on));
/** Qualidade do ar: pontos medidos e, como sub-produto, o modelo; religar volta à mesma seleção. */
const arSwitch = interruptor(useArStore, (s) => s.visivel, (on) => useArStore.getState().setVisivel(on));
const riverSwitch = interruptor(useRiverStore, (s) => s.visivel, (on) => useRiverStore.getState().setVisivel(on));
const stationSwitch = interruptor(useStationStore, (s) => s.visivel, (on) => useStationStore.getState().setVisivel(on));
const meteoSwitch = interruptor(useMeteoStore, (s) => s.visivel, (on) => useMeteoStore.getState().setVisivel(on));
const soundingSwitch = interruptor(useSoundingStore, (s) => s.visivel, (on) => useSoundingStore.getState().setVisivel(on));
const shipSwitch = interruptor(useShipStore, (s) => s.visivel, (on) => useShipStore.getState().setVisivel(on));
/** Mapa: alguma camada vetorial ligada. Desligar apaga todas; religar restaura a seleção (ou o padrão). */
const vectorSwitch = interruptor(useVectorStore, (s) => Object.values(s.visible).some(Boolean), (on) => useVectorStore.getState().setAllVisible(on));

// ---------------------------------------------------------------------------
// Resumos (blocos da folha do celular): o que está ligado em camadas com
// vários produtos. Os seletores devolvem string, então só re-renderizam
// quando o texto muda.
// ---------------------------------------------------------------------------

/** Nome curto de uma imagem: "Cor natural (GeoColor)" → "GeoColor". */
export function shortProductName(productId: string): string {
  const name = getProductName(productId);
  const paren = /\(([^)]+)\)/.exec(name);
  if (paren) return paren[1];
  return name.length > 18 ? `${name.slice(0, 17)}…` : name;
}

/** Satélite: a imagem de cima entre as visíveis, "+ n" para as demais. */
const useSatelliteSummary = () =>
  useGoesStore((s) => {
    const visible = s.layers.filter((l) => l.visible);
    if (visible.length === 0) return null;
    const top = shortProductName(visible[visible.length - 1].productId);
    return visible.length > 1 ? `${top} + ${visible.length - 1}` : top;
  });
const useRadarSummary = () => useRadarStore((s) => (s.visivel ? DEF_PRODUTO[s.produto].nome : null));
const useFireSummary = () =>
  useFireStore((s) => {
    const { goes10min, diario } = s.sources;
    if (goes10min && diario) return '2 fontes';
    if (goes10min) return 'GOES 10 min';
    return diario ? 'Diário' : null;
  });
/** Mapa: o nome da camada, se for uma só; senão quantas. */
const useVectorSummary = () =>
  useVectorStore((s) => {
    const on = VECTOR_LAYERS.filter((d) => s.visible[d.id]);
    if (on.length === 0) return null;
    if (on.length > 1) return `${on.length} camadas`;
    // O padrão é só "Nomes das cidades", longo demais para o bloco.
    return on[0].id === 'sedes' ? 'Cidades' : on[0].title;
  });

type ExportDialogComponent = typeof import('../export/ExportDialog').ExportDialog;

/**
 * Se o pedaço da exportação não carregar (típico após um novo deploy no GitHub
 * Pages: o arquivo antigo com hash sumiu), fecha o diálogo e mostra o aviso
 * "Nova versão disponível — Recarregar" (UpdateNotice). Não guardamos a
 * rejeição no React.lazy, para não ficar preso num erro.
 */
function ExportLoadFailed(): null {
  useEffect(() => {
    useUiStore.getState().set({ exportOpen: false, updateAvailable: true });
  }, []);
  return null;
}

// A exportação (codificadores de GIF/MP4) é pesada: só é baixada na 1ª vez que
// o diálogo abre. Se falhar, a próxima abertura tenta de novo (novo `lazy`).
let exportLoaded = false;
function makeExportLazy() {
  return lazy(() =>
    import('../export/ExportDialog')
      .then((m) => {
        exportLoaded = true;
        return { default: m.ExportDialog as ExportDialogComponent };
      })
      .catch((e: unknown) => {
        console.warn('[AmaView] Não foi possível carregar a exportação:', e);
        exportLazy = makeExportLazy();
        return { default: ExportLoadFailed as unknown as ExportDialogComponent };
      }),
  );
}
let exportLazy = makeExportLazy();

/** Aba "Queimadas". */
export function FirePanelSlot() {
  return (
    <ModuleBoundary name="focos de queimada">
      <FirePanel />
    </ModuleBoundary>
  );
}

/** Aba "Mapa". */
export function VectorPanelSlot() {
  return (
    <ModuleBoundary name="camadas do mapa">
      <VectorPanel />
    </ModuleBoundary>
  );
}

/** Aba "Radar". */
export function RadarPanelSlot() {
  return (
    <ModuleBoundary name="radares do SIPAM">
      <RadarPanel />
    </ModuleBoundary>
  );
}

/** Aba "Chuva" (por satélite). */
export function GsmapPanelSlot() {
  return (
    <ModuleBoundary name="chuva por satélite (GSMaP)">
      <GsmapPanel />
    </ModuleBoundary>
  );
}

/** Aba "Fumaça". */
export function SmokePanelSlot() {
  return (
    <ModuleBoundary name="fumaça do GOES-19">
      <SmokePanel />
    </ModuleBoundary>
  );
}

/** Aba "Raios". */
export function RaiosPanelSlot() {
  return (
    <ModuleBoundary name="raios do GOES-19">
      <RaiosPanel />
    </ModuleBoundary>
  );
}

/** Aba "Qualidade do ar". */
export function ArPanelSlot() {
  return (
    <ModuleBoundary name="qualidade do ar">
      <ArPanel />
    </ModuleBoundary>
  );
}

/** Aba "Estações". */
export function StationPanelSlot() {
  return (
    <ModuleBoundary name="estações do CEMADEN">
      <StationPanel />
    </ModuleBoundary>
  );
}

/** Aba "Tempo". */
export function MeteoPanelSlot() {
  return (
    <ModuleBoundary name="estações meteorológicas">
      <MeteoPanel />
    </ModuleBoundary>
  );
}

/** Aba "Nível". */
export function RiverPanelSlot() {
  return (
    <ModuleBoundary name="nível dos rios">
      <RiverPanel />
    </ModuleBoundary>
  );
}

/** Aba "Barcos". */
export function ShipPanelSlot() {
  return (
    <ModuleBoundary name="embarcações">
      <ShipPanel />
    </ModuleBoundary>
  );
}

registerPanel({ id: 'satelite', label: 'Satélite', icon: Satellite, Panel: SatellitePanel, order: 10, layer: satelliteSwitch, useSummary: useSatelliteSummary });
// Logo abaixo do Satélite: são imagens do mesmo tipo (uma grade animada no
// tempo, sobre o mapa), e o radar mostra por dentro a nuvem que o satélite vê
// por cima.
registerPanel({
  id: 'radar',
  label: 'Radar',
  icon: Radar,
  Panel: RadarPanelSlot,
  order: 15,
  useWarning: useRadarDesatualizado,
  tone: 'rio',
  layer: radarSwitch,
  useSummary: useRadarSummary,
  novoAte: '2026-12-31',
});
// Entre o Satélite e o Radar: é a chuva que os satélites estimam, sobre a
// imagem de um deles; o radar, logo abaixo, mede de perto a mesma chuva.
registerPanel({
  id: 'chuva',
  label: 'Chuva',
  icon: Droplets,
  Panel: GsmapPanelSlot,
  order: 12,
  useWarning: useGsmapDesatualizado,
  tone: 'rio',
  layer: gsmapSwitch,
  useSummary: useGsmapResumo,
  novoAte: '2026-12-31',
});
registerPanel({
  id: 'queimadas',
  label: 'Queimadas',
  icon: Flame,
  Panel: FirePanelSlot,
  order: 20,
  useWarning: useFiresInError,
  tone: 'fogo',
  layer: fireSwitch,
  useSummary: useFireSummary,
});
// Logo depois de Queimadas: o foco é o fogo no chão; a fumaça, o que sobe
// dele e viaja com o vento.
registerPanel({
  id: 'fumaca',
  label: 'Fumaça',
  icon: Haze,
  Panel: SmokePanelSlot,
  order: 22,
  useWarning: useFumacaDesatualizada,
  tone: 'fogo',
  layer: smokeSwitch,
  novoAte: '2026-12-31',
});
// Logo depois da Fumaça: o raio é a causa natural do fogo, e a ficha do foco
// usa os raios para dizer se ele caiu perto antes do foco.
registerPanel({
  id: 'raios',
  label: 'Raios',
  icon: Zap,
  Panel: RaiosPanelSlot,
  order: 22.5,
  useWarning: useRaiosDesatualizados,
  tone: 'fogo',
  layer: raiosSwitch,
  novoAte: '2027-01-31',
});
// Logo depois da Fumaça: a fumaça vista de cima pelo satélite, e o que ela
// faz com o ar que se respira embaixo.
registerPanel({
  id: 'ar',
  label: 'Qualidade do ar',
  icon: AirVent,
  Panel: ArPanelSlot,
  order: 23,
  useWarning: useArDesatualizado,
  tone: 'fogo',
  layer: arSwitch,
  novoAte: '2026-12-31',
});
/** Aba "Sondagem". */
export function SoundingPanelSlot() {
  return (
    <ModuleBoundary name="radiossonda">
      <SoundingPanel />
    </ModuleBoundary>
  );
}

// Entre Queimadas (20) e Pluviômetros (30): rio e chuva são a mesma história
// contada em dois tempos.
registerPanel({
  id: 'nivel',
  label: 'Nível',
  icon: Waves,
  Panel: RiverPanelSlot,
  order: 25,
  useWarning: useRiosDesatualizados,
  tone: 'rio',
  layer: riverSwitch,
  novoAte: '2026-12-31',
});
registerPanel({
  id: 'estacoes',
  label: 'Pluviômetros',
  icon: CloudRain,
  Panel: StationPanelSlot,
  order: 30,
  useWarning: useEstacoesDesatualizadas,
  tone: 'rio',
  layer: stationSwitch,
});
// Logo depois dos Pluviômetros (30) e antes da Sondagem (40): a superfície
// medida de hora em hora, entre a chuva do CEMADEN e o perfil do balão.
// "Tempo", e não "Estações meteorológicas": o trilho tem ~9 letras.
registerPanel({
  id: 'meteo',
  label: 'Tempo',
  icon: Thermometer,
  Panel: MeteoPanelSlot,
  order: 32,
  useWarning: useMeteoDesatualizado,
  tone: 'rio',
  layer: meteoSwitch,
  novoAte: '2026-12-31',
});
registerPanel({
  id: 'sondagem',
  label: 'Sondagem',
  icon: TowerControl,
  Panel: SoundingPanelSlot,
  order: 40,
  tone: 'rio',
  layer: soundingSwitch,
});
// "Próximos dias" não é painel do trilho: é modelo, não observação — tem entrada
// própria à direita (src/previsao/EntradaDaPrevisao, docs/proposta-previsao-entrada.md).
// Depois do par meteorológico (Pluviômetros 30 e Sondagem 40): navegação é
// outra categoria de leitura, e separá-las mantém o trilho legível.
registerPanel({
  id: 'barcos',
  label: 'Barcos',
  icon: Ship,
  Panel: ShipPanelSlot,
  order: 50,
  useWarning: useNaviosDesatualizados,
  tone: 'rio',
  layer: shipSwitch,
  novoAte: '2026-12-31',
});
registerPanel({ id: 'mapa', label: 'Mapa', icon: MapIcon, Panel: VectorPanelSlot, order: 90, layer: vectorSwitch, useSummary: useVectorSummary });

/** Diálogo "Exportar". */
export function ExportDialogSlot(props: { open: boolean; onClose: () => void }) {
  // Velocidade e pausa no fim atuais do loop viram o padrão da exportação.
  const fps = useLoopStore((s) => s.fps);
  const dwellMs = useLoopStore((s) => s.dwellMs);
  // Depois de carregado, fica montado (o diálogo guarda o próprio estado).
  if (!props.open && !exportLoaded) return null;
  const ExportDialog = exportLazy;
  return (
    <ModuleBoundary name="exportação" dialog={{ title: 'Exportar animação', open: props.open, onClose: props.onClose }}>
      <Suspense fallback={null}>
        <ExportDialog open={props.open} onClose={props.onClose} defaultFps={fps} defaultHoldSeconds={dwellMs / 1000} />
      </Suspense>
    </ModuleBoundary>
  );
}

/** Liga as camadas dos módulos ao mapa (chamado uma vez pelo App). */
export function useModuleLayers(): void {
  useFireLayers();
  // O município da busca e a localização (src/busca).
  useBuscaLayer();
  useRadarLayers();
  // Depois do radar: no clique, o eco do radar responde antes da estimativa do satélite.
  useGsmapLayers();
  useTrackingLayers();
  useRastreioChuvaLayers();
  // A previsão para o player principal e anda pelas antecedências (tracking/fluxo.ts).
  useEffect(() => iniciarFluxoPrevisao(), []);
  useSmokeLayers();
  useRaiosLayers();
  useArLayers();
  usePrevisaoLayers();
  useStationLayers();
  useSoundingLayers();
  useRiverLayers();
  useMeteoLayers();
  useShipLayers();
  useVectorLayers();
  // A cada ciclo da atualização automática os focos revalidam o arquivo diário
  // do INPE (cresce de hora em hora), mesmo quando a janela do loop não muda.
  useEffect(() => registerRefreshHook(refreshFires), []);
  // As estações revalidam pelo mesmo ciclo: o pipeline publica de 10 em 10 min.
  useEffect(() => registerRefreshHook(refreshEstacoes), []);
  useEffect(() => registerRefreshHook(refreshSondagens), []);
  // As embarcações publicam junto com a chuva, no mesmo ciclo de 15 min.
  useEffect(() => registerRefreshHook(refreshNavios), []);
  // As cotas publicam de hora em hora; revalidar a cada ciclo é barato.
  useEffect(() => registerRefreshHook(refreshRios), []);
  // Os radares publicam no ciclo da chuva (15 min); o manifesto tem ~15 KB.
  useEffect(() => registerRefreshHook(refreshRadar), []);
  // O rastreio de nuvens sai de 10 em 10 min; o índice volta 304 quando nada mudou.
  useEffect(() => registerRefreshHook(refreshRastreio), []);
  // O rastreio da chuva sai de 30 em 30 min, junto com o GSMaP.
  useEffect(() => registerRefreshHook(refreshRastreioChuva), []);
  // A fumaça sai de 10 em 10 min; o índice tem ~10 KB e volta 304 quando nada mudou.
  useEffect(() => registerRefreshHook(refreshFumaca), []);
  // Os raios saem a cada 20 s; o índice volta 304 quando nada mudou.
  useEffect(() => registerRefreshHook(refreshRaios), []);
  // A chuva por satélite sai de 30 em 30 min; o índice volta 304 quando nada mudou.
  useEffect(() => registerRefreshHook(refreshGsmap), []);
  // As estações meteorológicas saem a cada 5 min; os dois arquivos voltam 304 quando nada mudou.
  useEffect(() => registerRefreshHook(refreshMeteo), []);
  // O espelho da qualidade do ar publica de 5 em 5 min (~5 KB com gzip).
  useEffect(() => registerRefreshHook(refreshAr), []);
}
